const expandedRoutes = new Map();
/**
 * Converts a regex route pattern to an array of all possible Hono route variations.
 *
 * This function generates all possible route combinations by expanding optional segments.
 *
 * ## Conversion Rules
 *
 * ### Named Capture Groups with Specific Names
 * Named groups where the capture pattern matches the parameter name are treated
 * as required literal segments (not optional):
 * - `(?<new>new)` → `/new` (required literal, not `:new`)
 *
 * ### Named Capture Groups with Patterns
 * - `(?<uuid>.+)` → `/:uuid` (required wildcard parameter)
 * - `(?<id>[0-9]+)` → `/:id` (required parameter with constraint)
 *
 * ### Optional Groups
 * Each optional group generates route variations with and without that segment:
 * - Pattern with 2 optional segments generates 4 routes (2^2 combinations)
 *
 * ### Alternation Groups
 * Each alternative generates a separate route:
 * - `(finance|app/finance)` → separate routes for each option
 *
 * ## Examples
 *
 * ### Search Route
 * ```typescript
 * regexToRoutes('^\\/search(?:\\/(?<new>new))?(?:\\/(?<uuid>.+))?$')
 * // Returns: [
 * //   '/search',
 * //   '/search/new',
 * //   '/search/:uuid',
 * //   '/search/new/:uuid'
 * // ]
 * ```
 *
 * ### Finance Route with Alternations
 * ```typescript
 * regexToRoutes('^/(finance|app/finance)/?([^/]+)?(?:/([^/]+))?/?$')
 * // Returns: [
 * //   '/finance',
 * //   '/finance/:param1',
 * //   '/finance/:param1/:param2',
 * //   '/app/finance',
 * //   '/app/finance/:param1',
 * //   '/app/finance/:param1/:param2'
 * // ]
 * ```
 *
 * @param regexPattern - The regex pattern string to convert
 * @returns Array of all possible Hono route patterns
 */
export function regexToRoutes(regexPattern) {
    const cached = expandedRoutes.get(regexPattern);
    if (cached !== undefined)
        return [...cached];
    // Parse the regex pattern into segments
    let pattern = regexPattern;
    // Remove anchors and unescape
    pattern = pattern.replace(/^\^/, '').replace(/\$$/, '');
    pattern = pattern.replace(/\\\//g, '/');
    const segments = [];
    // For search pattern, we start with: /search
    // For finance pattern, we start with alternations
    // Extract alternation at the start: /(finance|app/finance)
    // The pattern is: /( alternatives )
    const alternationWithSlashMatch = pattern.match(/^\/\(([^)]+\|[^)]+)\)/);
    if (alternationWithSlashMatch) {
        const alternatives = alternationWithSlashMatch[1]?.split('|').map((a) => `/${a}`);
        segments.push({
            type: 'alternation',
            value: '',
            optional: false,
            alternatives,
        });
        pattern = pattern.substring(alternationWithSlashMatch[0].length);
    }
    else {
        // Extract base path (everything before first (? )
        const basePathMatch = pattern.match(/^([^(]+)/);
        if (basePathMatch && basePathMatch[1]) {
            const basePath = basePathMatch[1];
            segments.push({
                type: 'literal',
                value: basePath,
                optional: false,
            });
            pattern = pattern.substring(basePath.length);
        }
    }
    // Extract named capture groups and other segments
    let remainingPattern = pattern;
    let paramCounter = 0;
    let iterations = 0;
    const maxIterations = 100; // Safety limit
    while (remainingPattern.length > 0 && iterations++ < maxIterations) {
        // Try to match optional named capture group: (?:\/(?<name>pattern))?
        const optionalNamedMatch = remainingPattern.match(/^\(\?:\/\(\?<(\w+)>([^)]+)\)\)\?/);
        if (optionalNamedMatch) {
            const [fullMatch, name, capturePattern] = optionalNamedMatch;
            const isLiteral = capturePattern === name;
            segments.push({
                type: isLiteral ? 'literal' : 'param',
                value: isLiteral ? `/${name}` : `/:${name}`,
                optional: true,
                paramName: isLiteral ? undefined : name,
            });
            remainingPattern = remainingPattern.substring(fullMatch.length);
            continue;
        }
        // Try to match required named capture group: (?:\/(?<name>pattern))
        const requiredNamedMatch = remainingPattern.match(/^\(\?:\/\(\?<(\w+)>([^)]+)\)\)/);
        if (requiredNamedMatch) {
            const [fullMatch, name, capturePattern] = requiredNamedMatch;
            const isLiteral = capturePattern === name;
            segments.push({
                type: isLiteral ? 'literal' : 'param',
                value: isLiteral ? `/${name}` : `/:${name}`,
                optional: false,
                paramName: isLiteral ? undefined : name,
            });
            remainingPattern = remainingPattern.substring(fullMatch.length);
            continue;
        }
        // Try to match optional unnamed capture: /?([^/]+)? or (?:/([^/]+))?
        // Match patterns like: /?([^/]+)? or (?:/([^/]+))?
        const optionalUnnamed1 = remainingPattern.match(/^\/?\(([^)]+)\)\?/);
        const optionalUnnamed2 = remainingPattern.match(/^\(\?:\/\(([^)]+)\)\)\?/);
        const optionalUnnamedMatch = optionalUnnamed1 || optionalUnnamed2;
        if (optionalUnnamedMatch) {
            const [fullMatch] = optionalUnnamedMatch;
            paramCounter++;
            segments.push({
                type: 'param',
                value: `/:param${paramCounter}`,
                optional: true,
                paramName: `param${paramCounter}`,
            });
            remainingPattern = remainingPattern.substring(fullMatch.length);
            continue;
        }
        // Skip over trailing optional slashes and other noise
        const noiseMatch = remainingPattern.match(/^\/?\?+/);
        if (noiseMatch && noiseMatch[0].length > 0) {
            remainingPattern = remainingPattern.substring(noiseMatch[0].length);
            continue;
        }
        // If we can't match anything else, break
        break;
    }
    // Generate all combinations
    const routes = [];
    // Handle alternations
    const alternationSegment = segments.find((s) => s.type === 'alternation');
    const otherSegments = segments.filter((s) => s.type !== 'alternation');
    const baseAlternatives = alternationSegment
        ? alternationSegment.alternatives.map((alt) => `/${alt}`)
        : [''];
    // For each alternation (or just once if no alternations)
    for (const baseRoute of baseAlternatives) {
        // Generate all combinations of optional segments
        const optionalSegments = otherSegments.filter((s) => s.optional);
        const requiredSegments = otherSegments.filter((s) => !s.optional);
        // Check if we have unnamed sequential params (param1, param2, etc.)
        // These should be ordered. Named params can be in any combination.
        const hasUnnamedParams = optionalSegments.some((s) => s.paramName?.startsWith('param'));
        const allUnnamedParams = optionalSegments.every((s) => s.paramName?.startsWith('param'));
        if (allUnnamedParams && hasUnnamedParams) {
            // Generate ordered combinations for unnamed params
            // i.e., if you have param2, you must have param1
            const optionalCount = optionalSegments.length;
            for (let count = 0; count <= optionalCount; count++) {
                let route = baseRoute;
                // Add required segments first
                for (const seg of requiredSegments) {
                    route += seg.value;
                }
                // Add the first 'count' optional segments
                for (let j = 0; j < count; j++) {
                    route += optionalSegments[j]?.value ?? '';
                }
                route = route.replace(/\/+/g, '/');
                route = route.replace(/\/\?/g, ''); // Remove trailing /?
                route = route || '/';
                routes.push(route);
            }
        }
        else {
            // Generate power set for named params (all combinations)
            const optionalCount = optionalSegments.length;
            const combinationCount = Math.pow(2, optionalCount);
            for (let i = 0; i < combinationCount; i++) {
                let route = baseRoute;
                // Add required segments first
                for (const seg of requiredSegments) {
                    route += seg.value;
                }
                // Add optional segments based on binary representation
                for (let j = 0; j < optionalCount; j++) {
                    if (i & (1 << j)) {
                        route += optionalSegments[j]?.value ?? '';
                    }
                }
                route = route.replace(/\/+/g, '/');
                route = route.replace(/\/\?/g, ''); // Remove trailing /?
                route = route || '/';
                routes.push(route);
            }
        }
    }
    // Remove duplicates and sort by specificity (more specific first)
    const uniqueRoutes = [...new Set(routes)].sort((a, b) => {
        // Sort by specificity: literal segments before parameters
        const aSegments = a.split('/').filter(Boolean);
        const bSegments = b.split('/').filter(Boolean);
        // First, sort by number of segments (more specific = more segments)
        if (aSegments.length !== bSegments.length) {
            return bSegments.length - aSegments.length;
        }
        // Same number of segments, prioritize literals over parameters
        for (let i = 0; i < aSegments.length; i++) {
            const aIsParam = aSegments[i]?.startsWith(':');
            const bIsParam = bSegments[i]?.startsWith(':');
            // If one is a literal and the other is a param, literal comes first
            if (aIsParam && !bIsParam)
                return 1; // b (literal) comes before a (param)
            if (!aIsParam && bIsParam)
                return -1; // a (literal) comes before b (param)
            // Both are literals or both are params, compare alphabetically
            if (aSegments[i] !== bSegments[i]) {
                return aSegments[i].localeCompare(bSegments[i]);
            }
        }
        return 0;
    });
    expandedRoutes.set(regexPattern, uniqueRoutes);
    // Callers can mutate their result without changing subsequent expansions of this route.
    return [...uniqueRoutes];
}
//# sourceMappingURL=regex-to-routes.js.map